import { useTranslation } from 'react-i18next';
import { QUESTIONNAIRE_KEY_ROOT } from 'src/suitability/engine/i18n-questionnaire';
import type { RadioOption } from 'src/suitability/engine/components/radio-question';
import type { MultiSelectOption } from 'src/suitability/engine/components/multi-select-question';

export interface ConsilioOption {
  order: number;
  label: string;
  withText?: boolean;
}

export interface ConsilioAmount {
  label: string;
  suffix?: string;
}

export interface ConsilioQuestion {
  prompt: string;
  options?: ConsilioOption[];
  wealth?: ConsilioAmount;
  suffix?: string;
}

export interface ConsilioStep {
  key: string;
  label: string;
  title?: string;
  intro?: string;
}

export const useConsilioContent = () => {
  const { t } = useTranslation();

  const read = <T>(key: string): T | undefined => {
    const value = t(`${QUESTIONNAIRE_KEY_ROOT}.${key}`, {
      returnObjects: true,
    });

    return typeof value === 'string' ? undefined : (value as unknown as T);
  };

  return {
    step: (key: string): ConsilioStep =>
      read<ConsilioStep>(`steps.${key}`) ?? { key, label: key, title: key },
    question: (key: string): ConsilioQuestion =>
      read<ConsilioQuestion>(`questions.${key}`) ?? {
        prompt: key,
        options: [],
      },
  };
};

export const toRadioOptions = (
  questionKey: string,
  question: ConsilioQuestion,
): RadioOption[] =>
  (question.options ?? []).map((option) => ({
    id: `${questionKey}-${option.order}`,
    label: option.label,
    value: String(option.order),
    withText: option.withText,
  }));

export const toMultiSelectOptions = (
  question: ConsilioQuestion,
): MultiSelectOption[] =>
  (question.options ?? []).map((option) => ({
    id: String(option.order),
    label: option.label,
  }));
